// Push notification registration for the native (Capacitor) builds.
// No-op on the web. Called from NotificationsContext once a user is signed in.
//
// Flow: request permission → register with APNs/FCM → the OS hands back a
// device token → we store it via the register_device_token RPC (security
// definer, so a device switching accounts can re-claim its token). Delivery
// is server-side: a Database Webhook on `notifications` INSERT fires the
// send-push edge function, which fans out to this user's tokens.

import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';

let started = false;
let lastToken = null;

export async function initPush() {
  if (!Capacitor.isNativePlatform() || started) return;
  started = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    PushNotifications.addListener('registration', async ({ value }) => {
      lastToken = value;
      const { error } = await supabase.rpc('register_device_token', {
        p_token: value,
        p_platform: Capacitor.getPlatform(),
      });
      if (error) console.error('Failed to save push token:', error);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err);
    });

    // Tap on a lock-screen/banner notification → deep-link into the app.
    // data.type/related_id are set by the send-push edge function and mirror
    // the columns on the notifications table.
    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const data = notification?.data || {};
      if ((data.type === 'message' || data.type === 'match') && data.related_id) {
        window.location.href = `/chat/${data.related_id}`;
      } else if (data.type === 'like') {
        window.location.href = '/host-dashboard';
      } else {
        window.location.href = '/notifications';
      }
    });

    await PushNotifications.register();
  } catch (err) {
    console.error('Push init failed:', err);
  }
}

// Remove THIS device's push token on sign-out, so the next account on the phone
// doesn't receive the previous user's notifications. No-op on web / when never
// registered — and it deletes only by token, never by user_id (which would clear
// the user's OTHER devices too).
export async function unregisterDeviceToken() {
  if (!lastToken) return;
  try {
    await supabase.from('device_tokens').delete().eq('token', lastToken);
    lastToken = null;
  } catch (e) {
    console.error('Failed to unregister device token:', e);
  }
}
